import { useCallback, useEffect, useRef } from "react";

interface UseLongPressOptions {
  /** Milliseconds to hold before firing. */
  delay?: number;
  /** Called when the press is released before the delay elapses. */
  onShortPress?: () => void;
}

/**
 * Pointer long-press helper for touch and mouse. Fires `onLongPress` after
 * `delay` ms; suppresses the subsequent click so expand/tap handlers don't run.
 */
export function useLongPress(
  onLongPress: () => void,
  { delay = 520, onShortPress }: UseLongPressOptions = {},
) {
  const timerRef = useRef<number | null>(null);
  const longPressRef = useRef(false);
  const movedRef = useRef(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      longPressRef.current = false;
      movedRef.current = false;
      startRef.current = { x: event.clientX, y: event.clientY };
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        longPressRef.current = true;
        onLongPress();
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          navigator.vibrate(12);
        }
      }, delay);
    },
    [clearTimer, delay, onLongPress],
  );

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const start = startRef.current;
    if (!start || timerRef.current == null) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.hypot(dx, dy) > 10) {
      movedRef.current = true;
      clearTimer();
    }
  }, [clearTimer]);

  const onPointerUp = useCallback(() => {
    const wasLong = longPressRef.current;
    const wasMoved = movedRef.current;
    clearTimer();
    if (!wasLong && !wasMoved) {
      onShortPress?.();
    }
    startRef.current = null;
  }, [clearTimer, onShortPress]);

  const onPointerCancel = useCallback(() => {
    clearTimer();
    startRef.current = null;
  }, [clearTimer]);

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (longPressRef.current) {
      event.preventDefault();
      event.stopPropagation();
      longPressRef.current = false;
    }
  }, []);

  return {
    longPressActive: longPressRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave: onPointerCancel,
    onPointerCancel,
    onClickCapture,
  };
}
